// src/stores/theme.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import app from 'configs/appResolver';
import appService from 'services/appService';

import useZustand from 'hooks/useZustand';
import { logErrorSilent } from 'utils/errorLogger';
// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const localStorageKey = 'storage-theme';

/**
 * Get initial theme value with fallback chain:
 * 1. Theme store localStorage (currentTheme)
 * 2. App config THEME (set per-app in appcaup.js, applekkus.js, etc.)
 * 3. Default 'light'
 */
function getInitialTheme() {
  const storedTheme = getStorageThemeItem('currentTheme')?.toLowerCase();
  if (storedTheme === 'light' || storedTheme === 'dark') {
    return storedTheme;
  }
  const appTheme = app.THEME?.toLowerCase();
  if (appTheme === 'light' || appTheme === 'dark') {
    return appTheme;
  }
  return 'light';
}

/**
 * Normalize to BCP 47 format (pt-BR, en-US)
 * Accepts: pt-br, pt_BR, pt, BR, br, etc.
 */
function toBcp47Format(val = '') {
  const v = val.toString().trim().toLowerCase().replace('_', '-');

  // Already in format like pt-br or en-us
  if (v.includes('-')) {
    const [lang, region] = v.split('-');
    return `${lang}-${region.toUpperCase()}`;
  }

  // Just language code (pt, en) or country code (br, us)
  if (v === 'pt' || v === 'br') return 'pt-BR';
  if (v === 'en' || v === 'us') return 'en-US';
  if (v === 'es') return 'es-ES';

  return 'en-US'; // fallback
}

/**
 * Get initial language value with fallback chain (BCP 47 format: pt-BR, en-US):
 * 1. i18nextLng from localStorage
 * 2. Theme store localStorage (language)
 * 3. App config LANGUAGE
 * 4. Default 'en-US'
 */
function getInitialLanguage() {
  if (typeof window === 'undefined') return 'en-US';

  const i18nextLng = window.localStorage.getItem('i18nextLng');
  if (i18nextLng) return toBcp47Format(i18nextLng);

  const storedLanguage = getStorageThemeItem('language');
  if (storedLanguage) return toBcp47Format(storedLanguage);

  return toBcp47Format(app.LANGUAGE) || 'en-US';
}

/**
 * Get initial locale value (BCP 47 format: pt-BR, en-US):
 * Locale is for regional formatting (dates, numbers, currency)
 * Priority: localStorage -> app.LOCALE -> fallback
 */
function getInitialLocale() {
  const storedLocale = getStorageThemeItem('locale');
  if (storedLocale) return toBcp47Format(storedLocale);

  return toBcp47Format(app.LOCALE) || 'en-US';
}

// *******************************************************************************
// DEFAULTS
// *******************************************************************************

const DEFAULTS = {
  navType: 'top',
  currentTheme: app.THEME?.toLowerCase() === 'dark' ? 'dark' : 'light',
  sideNavTheme: 'light',
  headerNavColor: '',
  topNavColor: null,
  direction: 'ltr',
};

export const initialState = {
  // Navigation
  navCollapsed: getStorageThemeItem('navCollapsed') ?? true,
  navType: getStorageThemeItem('navType')?.toLowerCase() || DEFAULTS.navType,
  mobileNav: false,

  // Watermark
  watermark: {
    content: '',
    zIndex: -1,
  },

  // Theme & Colors
  currentTheme: getInitialTheme(),
  sideNavTheme: getStorageThemeItem('sideNavTheme')?.toLowerCase() || DEFAULTS.sideNavTheme, // 'light' or 'dark'
  headerNavColor: getStorageThemeItem('headerNavColor')?.toLowerCase() || DEFAULTS.headerNavColor,
  topNavColor: null,

  // Direction (RTL/LTR)
  direction: getStorageThemeItem('ltr')?.toLowerCase() || DEFAULTS.direction,

  // Layout
  blankLayout: false,
  settingsDrawerOpen: false,

  // Locale (BCP 47 format: pt-BR, en-US) - Regional formatting (dates, numbers, currency)
  locale: getInitialLocale(),

  // Language (BCP 47 format: pt-BR, en-US) - UI translation
  language: getInitialLanguage(),

  // Working state & loading
  working: 0,
  firstLoad: false,
  quote: null,
  quoteLoading: false,
  serverInfo: null,
  serverInfoLoading: false,

  // WebSocket
  wsMessage: null,

  // App paths
  APP_PREFIX_PATH: '/app',
  AUTH_PREFIX_PATH: '/auth',
  REDIRECT_URL_KEY: 'redirect',
};

// *******************************************************************************
// METHODS
// *******************************************************************************
function buildStorageKey() {
  return `${app.ID}:${localStorageKey}`;
}

export function getStorageThemeItem(key) {
  const field = buildStorageKey();
  let lsData;
  try {
    lsData = JSON.parse(localStorage.getItem(field));
  } catch (e) {
    logErrorSilent(e, {
      context: 'themeStore.getStorageThemeItem (JSON.parse)',
      extraData: { field, key },
    });
  }
  return lsData?.[key];
}

export function setStorageThemeItem(key, value) {
  const field = buildStorageKey();
  let lsData = {};
  try {
    lsData = JSON.parse(localStorage.getItem(field)) || {};
  } catch (e) {
    lsData = {};
  }
  if (value !== undefined && value !== null) {
    lsData[key] = value;
  } else {
    delete lsData[key];
  }
  localStorage.setItem(field, JSON.stringify(lsData));
}

export function removeStorageThemeItem(key) {
  const field = buildStorageKey();
  let lsData = {};
  try {
    lsData = JSON.parse(localStorage.getItem(field)) || {};
  } catch (e) {
    lsData = {};
  }
  if (lsData.hasOwnProperty(key)) {
    delete lsData[key];
    localStorage.setItem(field, JSON.stringify(lsData));
  }
}

// Initialize localStorage with default values if not present
function initializeStorage() {
  const field = buildStorageKey();
  const existingData = localStorage.getItem(field);

  // If data already exists, do nothing
  if (existingData) return;

  // If not, save default values
  const defaultValues = {
    navType: DEFAULTS.navType,
    currentTheme: DEFAULTS.currentTheme,
    sideNavTheme: DEFAULTS.sideNavTheme,
    headerNavColor: DEFAULTS.headerNavColor,
    topNavColor: DEFAULTS.topNavColor,
    direction: DEFAULTS.direction,
  };

  localStorage.setItem(field, JSON.stringify(defaultValues));
}

// *******************************************************************************
// STORE
// *******************************************************************************
initializeStorage();

export const useThemeStore = create(
  devtools(
    (set, get) => {
      // Bootstrap locale on store creation
      setTimeout(() => get().__bootstrapLocale(), 0);

      return {
        ...initialState,

        // watermark
        setWatermark: obj => set({ watermark: { ...obj } }),

        // ACTIONS: Navigation
        setNavCollapsed: collapsed => {
          setStorageThemeItem('navCollapsed', collapsed);
          set({ navCollapsed: collapsed });
        },

        setNavType: async navType => {
          setStorageThemeItem('navType', navType);
          set({ navType });

          // When switching to top navigation, ensure correct app color is used
          if (navType === 'top') {
            const { baseTheme: currentBaseTheme } = await import('configs/appEngine');
            set({ topNavColor: currentBaseTheme.colorPrimary });
          }
        },

        toggleMobileNav: mobileNav => {
          set({ mobileNav });
        },

        // ACTIONS: Theme & Colors
        switchTheme: theme => {
          // Save to theme store localStorage (persists even when logged out)
          setStorageThemeItem('currentTheme', theme);
          set({ currentTheme: theme });
        },

        setSideNavTheme: theme => {
          setStorageThemeItem('sideNavTheme', theme);
          set({ sideNavTheme: theme });
        },

        setHeaderNavColor: color => {
          set({ headerNavColor: color });
        },

        setTopNavColor: color => {
          set({ topNavColor: color });
        },

        // ACTIONS: Direction (RTL/LTR)
        setDirection: direction => {
          set({ direction });
          // Apply direction to HTML element
          document.documentElement.setAttribute('dir', direction);
        },

        // ACTIONS: Layout
        setBlankLayout: blank => {
          set({ blankLayout: blank });
        },

        setSettingsDrawerOpen: open => {
          set({ settingsDrawerOpen: open });
        },

        // ACTIONS: Locale (BCP 47 format: pt-BR, en-US)
        // Regional formatting (dates, numbers, currency)
        setLocale: async (value = app.LOCALE) => {
          // Normalize to BCP 47 format (pt-BR, en-US)
          const locale = toBcp47Format(value || app.LOCALE || 'en-US');

          try {
            // 1. Update axios header
            const { default: apiConfig } = await import('configs/fetchInterceptor');
            apiConfig.defaults.headers['x-locale'] = locale;

            // 2. Update localStorage
            setStorageThemeItem('locale', locale);
          } catch (err) {
            console.error('[setLocale] error:', err);
          }

          // 3. Update Zustand state
          set({ locale });
        },

        // ACTIONS: Language (BCP 47 format: pt-BR, en-US)
        // UI translation
        setLanguage: async value => {
          // Normalize to BCP 47 format (pt-BR, en-US)
          const bcp47 = toBcp47Format(value || 'en-US');

          try {
            // 1. Update i18next (uses BCP 47 directly)
            const { default: i18n } = await import('i18next');
            await i18n.changeLanguage(bcp47);

            // 2. Update dayjs locale (needs lowercase: pt-br, en-us)
            const dayjs = (await import('dayjs')).default;
            const dayjsLocale = bcp47.toLowerCase(); // pt-BR -> pt-br
            dayjs.locale(dayjsLocale);

            // 3. Update localStorage (BCP 47 format)
            setStorageThemeItem('language', bcp47);
            localStorage.setItem('i18nextLng', bcp47);

            // 4. Update axios headers (language only - locale doesn't change on language switch)
            const { default: apiConfig } = await import('configs/fetchInterceptor');
            apiConfig.defaults.headers['x-language'] = bcp47;
            apiConfig.defaults.headers['accept-language'] = bcp47;
            // Note: x-locale is set from app.LOCALE in __bootstrapLocale and doesn't change

            // 5. Fetch new quote for the selected language
            const fetchQuote = useThemeStore.getState().fetchQuote;
            if (fetchQuote) {
              fetchQuote();
            }
          } catch (err) {
            console.error('[setLanguage] error:', err);
          }

          // 6. Update Zustand state
          set({ language: bcp47 });
        },

        // ACTIONS: Quote
        setQuote: quote => {
          set({ quote, quoteLoading: false });
        },

        setQuoteLoading: loading => {
          set({ quoteLoading: loading });
        },

        fetchQuote: async () => {
          try {
            // Get current state
            const state = useThemeStore.getState();

            // Skip if already loading
            if (state.quoteLoading) {
              return;
            }

            const storedToken = useZustand('auth', 'token', false);
            if (storedToken) return;

            set({ quoteLoading: true, quoteDone: false });
            const quote = await appService.getQuote();
            set({ quote, quoteLoading: false, quoteDone: true });
          } catch (error) {
            // If request was aborted/cancelled (e.g., during logout), allow retry
            const wasCancelled =
              error?.code === 'ERR_CANCELED' || error?.name === 'AbortError' || error?.name === 'CanceledError';
            set({ quoteLoading: false, quoteDone: !wasCancelled });
          }
        },

        // ACTIONS: Server Info
        setServerInfo: serverInfo => {
          set({ serverInfo, serverInfoLoading: false });
        },

        setServerInfoLoading: loading => {
          set({ serverInfoLoading: loading });
        },

        fetchServerVersion: async () => {
          try {
            // Get current state
            const state = useThemeStore.getState();

            // Skip if already loading
            if (state.serverInfoLoading) {
              return;
            }

            set({ serverInfoLoading: true });
            const serverInfo = await appService.getServerVersion();
            set({ serverInfo, serverInfoLoading: false });
          } catch (error) {
            set({ serverInfoLoading: false });
          }
        },

        // ACTIONS: Working state
        setWorking: working => {
          set({ working });
          if (working === 0) {
            setTimeout(() => {
              set(state => {
                if (state.working === 0) {
                  return { firstLoad: true };
                }
                return {};
              });
            }, 300);
          }
        },

        setFirstLoad: firstLoad => {
          set({ firstLoad });
        },

        // ACTIONS: WebSocket
        setWsMessage: message => {
          set({ wsMessage: message });
        },

        // ACTIONS: Apply Theme Preferences from Backend
        applyThemePreferences: async themePreferences => {
          if (!themePreferences) return;

          // Get local theme from theme localStorage (set while logged out)
          const localTheme = getStorageThemeItem('currentTheme')?.toLowerCase();

          // Backend theme - empty/undefined means first login or no preference set yet
          const backendTheme = themePreferences.theme?.toLowerCase();

          // Determine which theme to use and whether to sync
          const isValidLocalTheme = localTheme === 'light' || localTheme === 'dark';
          const isValidBackendTheme = backendTheme === 'light' || backendTheme === 'dark';

          // CASE 1: Backend has no theme set yet (first login)
          // Sync local preference to backend
          if (isValidLocalTheme && !isValidBackendTheme) {
            try {
              await appService.updateAppPreferences({
                category: 'appearance',
                data: { theme: localTheme },
              });
              setStorageThemeItem('currentTheme', localTheme);
              set({ currentTheme: localTheme });
            } catch (e) {
              logErrorSilent(e, {
                context: 'themeStore.applyThemePreferences (sync local theme)',
                extraData: { localTheme },
              });
              setStorageThemeItem('currentTheme', 'light');
              set({ currentTheme: 'light' });
            }
          }
          // CASE 2: Local theme differs from backend theme
          // User changed theme while logged out - sync to backend
          else if (isValidLocalTheme && isValidBackendTheme && localTheme !== backendTheme) {
            try {
              await appService.updateAppPreferences({
                category: 'appearance',
                data: { theme: localTheme },
              });
              setStorageThemeItem('currentTheme', localTheme);
              set({ currentTheme: localTheme });
            } catch (e) {
              logErrorSilent(e, {
                context: 'themeStore.applyThemePreferences (sync local theme override)',
                extraData: { localTheme, backendTheme },
              });
              setStorageThemeItem('currentTheme', backendTheme);
              set({ currentTheme: backendTheme });
            }
          }
          // CASE 3: Use backend theme or default to light
          else {
            const themeToUse = isValidBackendTheme ? backendTheme : 'light';
            setStorageThemeItem('currentTheme', themeToUse);
            set({ currentTheme: themeToUse });
          }

          // Apply navigation type (side/top)
          // Priority: backend preference -> localStorage -> app default
          const backendNavigation = themePreferences.navigation?.toLowerCase();
          const localNavigation = getStorageThemeItem('navType')?.toLowerCase();
          const navigation =
            backendNavigation === 'side' || backendNavigation === 'top'
              ? backendNavigation
              : localNavigation === 'side' || localNavigation === 'top'
                ? localNavigation
                : app.NAVIGATION_TYPE || 'top';

          if (navigation === 'side' || navigation === 'top') {
            setStorageThemeItem('navType', navigation);
            set({ navType: navigation });

            // If top navigation, set default colors and uncollapse
            // Import baseTheme dynamically to ensure FORCE_APP is initialized
            if (navigation === 'top') {
              const { baseTheme: currentBaseTheme } = await import('configs/appEngine');
              set({ topNavColor: currentBaseTheme.colorPrimary, navCollapsed: false });
            }
          }

          // Apply nav collapsed state
          // Priority: backend preference -> localStorage -> default true
          const backendCollapsed = themePreferences.collapsed;
          const localCollapsed = getStorageThemeItem('navCollapsed');
          const collapsed =
            backendCollapsed !== undefined && backendCollapsed !== null
              ? backendCollapsed
              : localCollapsed !== undefined && localCollapsed !== null
                ? localCollapsed
                : true;
          setStorageThemeItem('navCollapsed', collapsed);
          set({ navCollapsed: collapsed });
        },

        // ACTIONS: Reset
        resetTheme: () => {
          removeStorageThemeItem(buildStorageKey());
          // removeStorageThemeItem('navType');
          // removeStorageThemeItem('navCollapsed');
          // removeStorageThemeItem('userPreferredTheme');
          set(initialState);
        },

        // BOOTSTRAP LOCALE (called on store creation)
        // Uses BCP 47 format (pt-BR, en-US) as single source of truth
        // LOCALE: Regional formatting (dates, numbers, currency) - from app.LOCALE
        // LANGUAGE: UI translation - user preference from storage or app.LANGUAGE
        __bootstrapLocale: async () => {
          try {
            const [{ default: dayjs }, { default: i18n }, { default: appConfig }] =
              await Promise.all([
                import('dayjs'),
                import('i18next'),
                import('configs/ConfigIndex'),
              ]);

            const [{ default: updateLocale }, { default: localizedFormat }] = await Promise.all([
              import('dayjs/plugin/updateLocale'),
              import('dayjs/plugin/localizedFormat'),
            ]);
            dayjs.extend(updateLocale);
            dayjs.extend(localizedFormat);

            await Promise.all([import('dayjs/locale/pt-br'), import('dayjs/locale/en'), import('dayjs/locale/es')]);

            // LOCALE: Regional formatting (dates, numbers, currency)
            // Priority: localStorage -> app.LOCALE -> fallback
            const storedLocale = getStorageThemeItem('locale');
            const locale = toBcp47Format(storedLocale || appConfig?.LOCALE || 'en-US');
            const localeCountryCode = locale.split('-')[1] || 'US';

            // LANGUAGE: UI translation - user preference
            // Priority: i18nextLng -> language storage -> app.LANGUAGE -> fallback
            const i18nextLng = localStorage?.getItem('i18nextLng');
            const languageFromStorage = getStorageThemeItem('language');
            const declaredLanguage = appConfig?.LANGUAGE;
            const language = toBcp47Format(
              i18nextLng || languageFromStorage || declaredLanguage || 'en-US'
            );

            // 1. i18next uses LANGUAGE for UI translation
            if (i18n.language !== language) await i18n.changeLanguage(language);

            // 2. dayjs uses LOCALE for regional formatting (dates, numbers)
            // Configure date formats based on locale's region
            if (localeCountryCode === 'BR') {
              dayjs.updateLocale('pt-br', {
                formats: {
                  LT: 'HH:mm',
                  LTS: 'HH:mm:ss',
                  L: 'DD/MM/YYYY',
                  LL: 'D MMMM YYYY',
                  LLL: 'D MMMM YYYY HH:mm',
                  LLLL: 'dddd, D MMMM YYYY HH:mm',
                },
              });
              // English UI with Brazilian date format
              dayjs.updateLocale('en', {
                formats: {
                  LT: 'HH:mm',
                  LTS: 'HH:mm:ss',
                  L: 'DD/MM/YYYY',
                  LL: 'D MMMM YYYY',
                  LLL: 'D MMMM YYYY HH:mm',
                  LLLL: 'dddd, D MMMM YYYY HH:mm',
                },
              });
            } else {
              dayjs.updateLocale('en', { formats: { L: 'MM/DD/YYYY' } });
              dayjs.updateLocale('pt-br', { formats: { L: 'MM/DD/YYYY' } });
            }

            // Set dayjs locale based on LANGUAGE for month/day names
            // but formatting will use LOCALE's date format configured above
            const dayjsLocale = language.toLowerCase();
            dayjs.locale(dayjsLocale);

            // 3. Persist language (BCP 47 format)
            try {
              localStorage.setItem('i18nextLng', language);
              setStorageThemeItem('language', language);
            } catch (_) {}

            // 4. Update axios headers with locale and language
            // x-locale: Regional formatting (dates, numbers, currency) - for emails
            // x-language: UI translation language
            try {
              const { default: apiConfig } = await import('configs/fetchInterceptor');
              apiConfig.defaults.headers['x-locale'] = locale;
              apiConfig.defaults.headers['x-language'] = language;
              apiConfig.defaults.headers['accept-language'] = language;
            } catch (_) {}

            // 5. Update Zustand state
            set({ locale, language });
          } catch (err) {
            console.error('[themeStore locale bootstrap] error:', err);
          }
        },
      };
    },
    { name: 'theme-store' }
  )
);

// *******************************************************************************
// SELECTORS (For non-React contexts)
// *******************************************************************************
export const fromThemeStore = selector => {
  if (typeof selector === 'function') {
    return selector(useThemeStore.getState());
  }
  return useThemeStore.getState();
};
